import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Phone, Globe, Clock, Star, Navigation, ChevronLeft, ChevronRight, Instagram, Youtube, Facebook, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/use-language';
import type { LocalizedText } from '@/lib/products';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DealerProduct {
  id: string;
  image: string;
  name: LocalizedText;
}

export interface DealerCardData {
  id: string;
  name: string;
  type: string;
  subtitle?: LocalizedText;
  description?: LocalizedText;
  address: LocalizedText | string;
  phone: string;
  website?: string;
  hours?: string;
  rating?: number;
  coordinates: [number, number];
  photos?: string[];
  products?: DealerProduct[];
  instagramUrl?: string;
  youtubeUrl?: string;
  tiktokUrl?: string;
  facebookUrl?: string;
}

interface MobileDealerCardProps {
  dealer: DealerCardData | null;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const cleanPhone = (phone: string) => phone?.replace(/[^\d+]/g, '') ?? '';

function Stars({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={13}
          className={cn(
            'transition-colors',
            i < full ? 'fill-[#B08A5A] text-[#B08A5A]' : i === full && half ? 'fill-[#B08A5A]/50 text-[#B08A5A]' : 'fill-white/10 text-white/10'
          )}
        />
      ))}
      <span className="text-[11px] font-bold text-white/60 ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

function SocialBtn({ href, icon: Icon, label }: { href: string; icon: React.ElementType; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
    >
      <Icon size={16} />
    </a>
  );
}

// TikTok icon (not in lucide)
function TikTokIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z"/>
    </svg>
  );
}

// ─── Photo Lightbox ───────────────────────────────────────────────────────────

function PhotoLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-lg" onClick={onClose} />
      <motion.img
        src={src}
        alt="Photo"
        className="relative z-10 max-w-full max-h-full object-contain rounded-xl"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        style={{ maxHeight: '90dvh', maxWidth: '95vw' }}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white"
      >
        <X size={18} />
      </button>
    </motion.div>
  );
}

// ─── Photo Gallery ────────────────────────────────────────────────────────────

function PhotoGallery({ photos }: { photos: string[] }) {
  const [current, setCurrent] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const touchStartX = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (photos.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % photos.length);
    }, 5000);
  }, [photos.length]);

  useEffect(() => {
    setCurrent(0);
    resetInterval();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [resetInterval]);

  const go = useCallback((idx: number) => {
    setCurrent((idx + photos.length) % photos.length);
    resetInterval();
  }, [photos.length, resetInterval]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      go(dx < 0 ? current + 1 : current - 1);
    }
    touchStartX.current = null;
  };

  if (!photos.length) return (
    <div className="w-full h-full bg-white/5 flex items-center justify-center">
      <MapPin size={32} className="text-white/20" />
    </div>
  );

  return (
    <>
      <div
        className="relative w-full h-full overflow-hidden select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.img
            key={photos[current]}
            src={photos[current]}
            alt="Photo"
            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setLightboxSrc(photos[current])}
            draggable={false}
          />
        </AnimatePresence>

        {/* Gradient */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Nav arrows */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); go(current - 1); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-black/60 transition-all"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(current + 1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-black/60 transition-all"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}

        {/* Dots */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
            {photos.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'rounded-full transition-all duration-300',
                  i === current ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40'
                )}
              />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightboxSrc && <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      </AnimatePresence>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const MobileDealerCard: React.FC<MobileDealerCardProps> = ({ dealer, onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();

  // Helper: get localized string with RU fallback
  const loc = (field: LocalizedText | undefined): string => {
    if (!field) return '';
    return field[language as keyof LocalizedText] || field.ru || '';
  };

  // "Open until" label — UZ uses different word order: "20:00 gacha ochiq"
  const formatHours = (hours: string): string => {
    if (language === 'uz') return `${hours} ${t('wtb.card.openUntil')}`;
    return `${t('wtb.card.openUntil')} ${hours}`;
  };

  // Scroll to top when dealer changes
  useEffect(() => {
    if (dealer && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [dealer?.id]);

  // Lock body scroll while open
  useEffect(() => {
    if (!dealer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [dealer]);

  // Only use photos from the dealer — no static fallback
  const photos = dealer?.photos?.length ? dealer.photos : [];

  const routeUrl = dealer
    ? `https://yandex.ru/maps/?mode=routes&rtext=~${dealer.coordinates.join(',')}&rtt=auto`
    : '#';

  const hasSocials = !!(dealer?.instagramUrl || dealer?.youtubeUrl || dealer?.tiktokUrl || dealer?.facebookUrl);
  const hasProducts = !!(dealer?.products?.length);

  return (
    <AnimatePresence>
      {dealer && (
        <motion.div
          key={`dealer-card-${dealer.id}`}
          className="fixed inset-0 z-[200] bg-[#0D0B08] flex flex-col"
          initial={{ y: '100%', opacity: 1 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 280, mass: 0.8 }}
        >
          {/* ── ZONE 1: Fixed top — Photo Gallery ── */}
          <div className="flex-none relative" style={{ height: '44vw', minHeight: 180, maxHeight: 260 }}>
            <PhotoGallery photos={photos} />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white"
            >
              <X size={16} />
            </button>

            {/* Swipe indicator */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-white/30" />
          </div>

          {/* ── ZONE 2: Scrollable center ── */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              scrollBehavior: 'smooth',
            }}
          >
            <div className="px-5 pt-5 pb-4 space-y-6">

              {/* Store info block */}
              <div className="space-y-2">
                <h2 className="text-2xl font-black uppercase tracking-tight leading-tight text-white">
                  {dealer.name}
                </h2>
                {(() => {
                  const sub = dealer.subtitle
                    ? (loc(dealer.subtitle) ||
                       (dealer.subtitle as any).ru ||
                       (dealer.subtitle as any).en ||
                       (dealer.subtitle as any).uz ||
                       (typeof dealer.subtitle === 'string' ? dealer.subtitle : ''))
                    : '';
                  return sub ? (
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#B08A5A]">{sub}</p>
                  ) : null;
                })()}
                {dealer.description && loc(dealer.description) && (
                  <p className="text-[13px] text-white/40 leading-relaxed" style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{loc(dealer.description)}</p>
                )}
                {dealer.rating !== undefined && dealer.rating > 0 && (
                  <Stars rating={dealer.rating} />
                )}
                {dealer.hours && (
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-400">
                    <Clock size={12} />
                    {formatHours(dealer.hours)}
                  </div>
                )}
              </div>

              {/* Separator */}
              <div className="border-t border-white/8" />

              {/* Products & Services — only if data exists */}
              {hasProducts && (
                <>
                  <div className="space-y-3">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-white/40">
                      {t('wtb.card.productsServices')}
                    </h3>
                    {/* Catalog-style product cards — horizontal scroll */}
                    <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {dealer.products!.map(product => (
                        <div
                          key={product.id}
                          className="flex-none relative overflow-hidden bg-[#1A1A1A] border border-white/5 group"
                          style={{ width: 'calc(42vw - 1.25rem)', aspectRatio: '2/3.5' }}
                        >
                          {/* Full-bleed image */}
                          <div className="absolute inset-0">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={loc(product.name)}
                                draggable={false}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-white/5">
                                <MapPin size={20} className="text-white/20" />
                              </div>
                            )}
                            {/* Bottom gradient for readability */}
                            <div
                              className="absolute inset-x-0 bottom-0 h-[50%] pointer-events-none"
                              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0) 100%)' }}
                            />
                            {/* Mirror/shine overlay */}
                            <div className="mirror-reflection opacity-30" />
                            <div className="shine-overlay" />
                          </div>

                          {/* Content overlay */}
                          <div className="relative h-full flex flex-col z-10">
                            <div className="flex-1" />
                            {/* Bottom info */}
                            <div className="p-2 space-y-1.5">
                              <h4 className="text-[8px] font-black uppercase tracking-tight text-white leading-tight line-clamp-2">
                                {loc(product.name)}
                              </h4>
                              <div className="h-px bg-[#B08A5A]/40 w-full" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Separator */}
                  <div className="border-t border-white/8" />
                </>
              )}

              {/* Contacts */}
              <div className="space-y-3">
                <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-white/40">
                  {t('wtb.card.contacts')}
                </h3>
                <div className="space-y-2.5">
                  {/* Address */}
                  {(typeof dealer.address === 'string' ? dealer.address : (dealer.address?.ru || dealer.address?.en || dealer.address?.uz)) && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                        <MapPin size={14} className="text-[#B08A5A]" />
                      </div>
                      <div className="flex-1 min-w-0 pt-1.5">
                        <p className="text-sm text-white/70 leading-snug">{typeof dealer.address === 'string' ? dealer.address : (dealer.address[language as 'ru' | 'en' | 'uz'] || dealer.address.ru)}</p>
                      </div>
                    </div>
                  )}
                  {/* Phone */}
                  {dealer.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                        <Phone size={14} className="text-[#B08A5A]" />
                      </div>
                      <a href={`tel:${cleanPhone(dealer.phone)}`} className="text-sm text-white/70 hover:text-white transition-colors">
                        {dealer.phone}
                      </a>
                    </div>
                  )}
                  {/* Website */}
                  {dealer.website && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                        <Globe size={14} className="text-[#B08A5A]" />
                      </div>
                      <a
                        href={dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#B08A5A] hover:text-white transition-colors truncate"
                      >
                        {dealer.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Social links */}
                {hasSocials && (
                  <div className="pt-1 space-y-2">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-white/30">
                      {t('wtb.card.socials')}
                    </h4>
                    <div className="flex gap-2.5">
                      {dealer.instagramUrl && (
                        <SocialBtn href={dealer.instagramUrl} icon={Instagram} label="Instagram" />
                      )}
                      {dealer.youtubeUrl && (
                        <SocialBtn href={dealer.youtubeUrl} icon={Youtube} label="YouTube" />
                      )}
                      {dealer.tiktokUrl && (
                        <a
                          href={dealer.tiktokUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="TikTok"
                          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all"
                        >
                          <TikTokIcon size={16} />
                        </a>
                      )}
                      {dealer.facebookUrl && (
                        <SocialBtn href={dealer.facebookUrl} icon={Facebook} label="Facebook" />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom padding to clear fixed footer */}
              <div style={{ height: 96 }} />
            </div>
          </div>

          {/* ── ZONE 3: Fixed bottom — Action buttons ── */}
          <div
            className="flex-none border-t border-white/8 bg-[#0D0B08] px-4 py-3 grid grid-cols-3 gap-2"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            {/* Route */}
            <a
              href={routeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-[#B08A5A] text-white font-black text-[9px] uppercase tracking-widest shadow-[0_0_20px_rgba(176,138,90,0.3)] active:scale-95 transition-transform"
            >
              <Navigation size={16} />
              {t('wtb.card.route')}
            </a>

            {/* Call */}
            {dealer?.phone ? (
              <a
                href={`tel:${cleanPhone(dealer.phone)}`}
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-white/8 border border-white/10 text-white font-black text-[9px] uppercase tracking-widest active:scale-95 transition-transform"
              >
                <Phone size={16} />
                {t('wtb.card.call')}
              </a>
            ) : (
              <button
                disabled
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-white/5 border border-white/5 text-white/20 font-black text-[9px] uppercase tracking-widest cursor-not-allowed"
              >
                <Phone size={16} />
                {t('wtb.card.call')}
              </button>
            )}

            {/* Website */}
            {dealer?.website ? (
              <a
                href={dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-white/8 border border-white/10 text-white font-black text-[9px] uppercase tracking-widest active:scale-95 transition-transform"
              >
                <ExternalLink size={16} />
                <span className="leading-none">{t('wtb.card.website')}</span>
              </a>
            ) : (
              <button
                disabled
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl bg-white/5 border border-white/5 text-white/20 font-black text-[9px] uppercase tracking-widest cursor-not-allowed"
              >
                <ExternalLink size={16} />
                <span className="leading-none">{t('wtb.card.website')}</span>
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
